/** Classifies a thrown pi-server AI-chat error (boundary-narrowed field-by-field) into a calm, user-facing in-chat descriptor. Never throws. */

export type ChatApiErrorKind = "rate-limit" | "unavailable" | "session-terminated" | "out-of-sync" | "not-available" | "unknown"

export interface ChatApiErrorDescriptor {
  kind: ChatApiErrorKind
  message: string
}

const MESSAGES: Record<ChatApiErrorKind, string> = {
  "rate-limit": "You've reached the usage limit for now. Please wait a moment and try again.",
  unavailable: "The assistant is temporarily unavailable. Please try again in a little while.",
  "session-terminated": "This conversation has ended. Start a new chat to keep going.",
  "out-of-sync": "This message is out of sync with the current thread. Please refresh the thread and try again.",
  "not-available": "This conversation is no longer available.",
  unknown: "Something went wrong on our end. Please try again.",
}

/** The numeric HTTP-ish `code` the client error classes stamp on themselves. */
function statusCodeOf(error: unknown): number | null {
  if (typeof error !== "object" || error === null || !("code" in error)) return null
  const { code } = error as { code: unknown }
  return typeof code === "number" ? code : null
}

/** The `retryAfter` header value `TooManyRequestsError` carries, when present. */
function retryAfterOf(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("retryAfter" in error)) return null
  const { retryAfter } = error as { retryAfter: unknown }
  return typeof retryAfter === "string" && retryAfter.length > 0 ? retryAfter : null
}

/** The op-summarize error `code` string riding on a `TechnicalError`/`ConflictError` body. */
function bodyCodeOf(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("error" in error)) return null
  const body: unknown = (error as { error: unknown }).error
  if (typeof body !== "object" || body === null || !("code" in body)) return null
  const { code } = body as { code: unknown }
  return typeof code === "string" ? code : null
}

function matches(bodyCode: string | null, needle: string): boolean {
  return bodyCode != null && bodyCode.toUpperCase().includes(needle)
}

/** Classify a thrown chat API error into a calm descriptor. Never throws. */
export function describeChatApiError(error: unknown): ChatApiErrorDescriptor {
  const status = statusCodeOf(error)
  const bodyCode = bodyCodeOf(error)

  // Rate limit / token budget: the 429 status, or an explicit budget/rate code on the body.
  if (status === 429 || matches(bodyCode, "RATE_LIMIT") || matches(bodyCode, "BUDGET")) {
    const retryAfter = retryAfterOf(error)
    const seconds = retryAfter != null ? Number.parseInt(retryAfter, 10) : Number.NaN
    if (Number.isFinite(seconds) && seconds > 0) {
      return { kind: "rate-limit", message: `You've reached the usage limit. Please try again in about ${seconds} seconds.` }
    }
    return { kind: "rate-limit", message: MESSAGES["rate-limit"] }
  }

  // A genuinely ended session: an explicit session/terminated code on the body (checked before 409, which is recoverable).
  if (matches(bodyCode, "SESSION") || matches(bodyCode, "TERMINAT")) {
    return { kind: "session-terminated", message: MESSAGES["session-terminated"] }
  }

  // Out-of-sync continue cursor (409 conflict): the thread moved on, recoverable by refreshing — NOT a dead session.
  if (status === 409 || matches(bodyCode, "CONFLICT")) {
    return { kind: "out-of-sync", message: MESSAGES["out-of-sync"] }
  }

  // Deleted / inaccessible thread.
  if (status === 404 || matches(bodyCode, "NOT_FOUND")) {
    return { kind: "not-available", message: MESSAGES["not-available"] }
  }

  // Service unavailable / any other server-side failure (503, 5xx, TechnicalError).
  if (status != null && status >= 500) {
    return { kind: "unavailable", message: MESSAGES.unavailable }
  }
  if (matches(bodyCode, "UNAVAILABLE")) {
    return { kind: "unavailable", message: MESSAGES.unavailable }
  }

  return { kind: "unknown", message: MESSAGES.unknown }
}
